'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  CreditCard,
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingUp,
  Filter,
  RefreshCw,
  BarChart2,
  MapPin,
  Clock,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  CartesianGrid,
} from 'recharts';

// ─── Mock Data ────────────────────────────────────────────────────────────────

const PAYMENT_RECORDS = [
  { id: 'PMT-2026-004821', taxpayer: 'Kollie Trading Co.', billRef: 'BILL-2026-0091', billed: 84200, received: 84200, channel: 'Bank Transfer', county: 'Montserrado', date: '2026-07-06', status: 'matched' },
  { id: 'PMT-2026-004820', taxpayer: 'Gedeh Commerce Ltd.', billRef: 'BILL-2026-0088', billed: 18300, received: 15000, channel: 'Orange Money', county: 'Grand Gedeh', date: '2026-07-06', status: 'underpayment' },
  { id: 'PMT-2026-004819', taxpayer: 'Maryland Fisheries Ltd.', billRef: 'BILL-2026-0085', billed: 22800, received: 22800, channel: 'Bank Transfer', county: 'Maryland', date: '2026-07-05', status: 'matched' },
  { id: 'PMT-2026-004818', taxpayer: 'Fatima Kamara', billRef: 'BILL-2026-0082', billed: 4800, received: 5200, channel: 'MTN Money', county: 'Lofa', date: '2026-07-05', status: 'overpayment' },
  { id: 'PMT-2026-004817', taxpayer: 'LPA Holdings Ltd.', billRef: 'BILL-2026-0079', billed: 156400, received: 0, channel: 'Bank Transfer', county: 'Grand Bassa', date: '2026-07-04', status: 'unmatched' },
  { id: 'PMT-2026-004816', taxpayer: 'Bong County Dev. Corp.', billRef: 'BILL-2026-0076', billed: 31700, received: 28000, channel: 'Orange Money', county: 'Bong', date: '2026-07-04', status: 'underpayment' },
  { id: 'PMT-2026-004815', taxpayer: 'Margibi Industrial Corp.', billRef: 'BILL-2026-0073', billed: 47500, received: 47500, channel: 'MTN Money', county: 'Margibi', date: '2026-07-03', status: 'matched' },
  { id: 'PMT-2026-004814', taxpayer: 'Nimba Agri Holdings', billRef: 'BILL-2026-0070', billed: 12600, received: 0, channel: 'Orange Money', county: 'Nimba', date: '2026-07-03', status: 'discrepancy' },
  { id: 'PMT-2026-004813', taxpayer: 'Cape Mount Realty', billRef: 'BILL-2026-0067', billed: 9200, received: 9200, channel: 'Bank Transfer', county: 'Grand Cape Mount', date: '2026-07-02', status: 'matched' },
  { id: 'PMT-2026-004812', taxpayer: 'Rivercess Timber Co.', billRef: 'BILL-2026-0064', billed: 33100, received: 20000, channel: 'MTN Money', county: 'Rivercess', date: '2026-07-02', status: 'underpayment' },
];

const DAILY_VELOCITY = [
  { day: 'Mon', orangeMoney: 42, mtn: 31, bank: 68 },
  { day: 'Tue', orangeMoney: 55, mtn: 44, bank: 82 },
  { day: 'Wed', orangeMoney: 38, mtn: 29, bank: 71 },
  { day: 'Thu', orangeMoney: 61, mtn: 52, bank: 94 },
  { day: 'Fri', orangeMoney: 74, mtn: 63, bank: 110 },
  { day: 'Sat', orangeMoney: 28, mtn: 19, bank: 34 },
  { day: 'Sun', orangeMoney: 15, mtn: 11, bank: 22 },
];

const WEEKLY_VELOCITY = [
  { week: 'W1 Jun', orangeMoney: 210, mtn: 178, bank: 390 },
  { week: 'W2 Jun', orangeMoney: 248, mtn: 201, bank: 445 },
  { week: 'W3 Jun', orangeMoney: 195, mtn: 163, bank: 412 },
  { week: 'W4 Jun', orangeMoney: 271, mtn: 224, bank: 501 },
  { week: 'W1 Jul', orangeMoney: 313, mtn: 249, bank: 579 },
];

const COUNTY_VELOCITY = [
  { county: 'Montserrado', total: 1842, matched: 1680, flags: 12 },
  { county: 'Nimba', total: 412, matched: 380, flags: 8 },
  { county: 'Bong', total: 318, matched: 290, flags: 5 },
  { county: 'Lofa', total: 241, matched: 225, flags: 4 },
  { county: 'Grand Bassa', total: 198, matched: 170, flags: 9 },
  { county: 'Margibi', total: 187, matched: 175, flags: 3 },
  { county: 'Maryland', total: 143, matched: 138, flags: 2 },
];

const statusConfig: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  matched: { label: 'Matched', color: '#34d399', bg: 'rgba(52,211,153,0.12)', icon: <CheckCircle size={12} /> },
  underpayment: { label: 'Underpayment', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)', icon: <AlertTriangle size={12} /> },
  overpayment: { label: 'Overpayment', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', icon: <TrendingUp size={12} /> },
  unmatched: { label: 'Unmatched', color: '#f87171', bg: 'rgba(248,113,113,0.12)', icon: <XCircle size={12} /> },
  discrepancy: { label: 'Discrepancy', color: '#e879f9', bg: 'rgba(232,121,249,0.12)', icon: <AlertTriangle size={12} /> },
};

const channelColors: Record<string, string> = {
  'Orange Money': '#f59e0b',
  'MTN Money': '#fbbf24',
  'Bank Transfer': '#60a5fa',
};

const kpis = [
  { label: 'Payments Today', value: '247', sub: '+18 vs yesterday', color: 'var(--accent)', icon: CreditCard },
  { label: 'Matched', value: '198', sub: '80.2% match rate', color: '#34d399', icon: CheckCircle },
  { label: 'Flagged', value: '34', sub: 'Underpayments & discrepancies', color: '#fbbf24', icon: AlertTriangle },
  { label: 'Unmatched', value: '15', sub: 'No bill reference found', color: '#f87171', icon: XCircle },
];

type VelocityView = 'daily' | 'weekly';
type StatusFilter = 'all' | 'matched' | 'underpayment' | 'overpayment' | 'unmatched' | 'discrepancy';
type ChannelFilter = 'All' | 'Orange Money' | 'MTN Money' | 'Bank Transfer';

export default function PropertyPaymentsMatchingClient() {
  const [velocityView, setVelocityView] = useState<VelocityView>('daily');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('All');
  const [activeTab, setActiveTab] = useState<'matching' | 'velocity' | 'county'>('matching');

  const filtered = PAYMENT_RECORDS.filter((p) => {
    const statusOk = statusFilter === 'all' || p.status === statusFilter;
    const channelOk = channelFilter === 'All' || p.channel === channelFilter;
    return statusOk && channelOk;
  });

  const velocityData = velocityView === 'daily' ? DAILY_VELOCITY : WEEKLY_VELOCITY;
  const velocityKey = velocityView === 'daily' ? 'day' : 'week';

  return (
    <AppLayout>
      <div className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)' }}
            >
              <CreditCard size={20} style={{ color: '#070e1f' }} />
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{ color: 'var(--dark-text)' }}>
                Property Payments — Stage 8
              </h1>
              <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>
                Payment matching · Discrepancy flagging · Collection velocity by channel & county
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ backgroundColor: 'rgba(52,211,153,0.15)', color: '#34d399', border: '1px solid rgba(52,211,153,0.3)' }}
            >
              <RefreshCw size={11} />
              Live Sync Active
            </div>
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ backgroundColor: 'rgba(212,160,23,0.12)', color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.3)' }}
            >
              <Clock size={11} />
              Last sync: 2 min ago
            </div>
          </div>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {kpis.map((k) => {
            const KIcon = k.icon;
            return (
              <div
                key={k.label}
                className="rounded-xl px-4 py-4 flex items-center gap-3"
                style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${k.color}18` }}
                >
                  <KIcon size={18} style={{ color: k.color }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{k.label}</p>
                  <p className="text-xl font-bold font-mono-data" style={{ color: k.color }}>{k.value}</p>
                  <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{k.sub}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div
          className="rounded-xl overflow-hidden"
          style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
        >
          <div className="flex border-b overflow-x-auto scrollbar-thin" style={{ borderColor: 'var(--dark-border)' }}>
            {([
              { key: 'matching', label: 'Payment Matching', icon: <CheckCircle size={14} /> },
              { key: 'velocity', label: 'Collection Velocity', icon: <BarChart2 size={14} /> },
              { key: 'county', label: 'County Breakdown', icon: <MapPin size={14} /> },
            ] as const).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap transition-all duration-150 border-b-2"
                style={{
                  borderColor: activeTab === tab.key ? 'var(--accent)' : 'transparent',
                  color: activeTab === tab.key ? 'var(--accent)' : 'var(--dark-muted)',
                  backgroundColor: activeTab === tab.key ? 'rgba(212,160,23,0.05)' : 'transparent',
                }}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-5">
            {/* ── Payment Matching Tab ── */}
            {activeTab === 'matching' && (
              <div>
                {/* Filters */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <div className="flex items-center gap-1.5 mr-2">
                    <Filter size={13} style={{ color: 'var(--dark-muted)' }} />
                    <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Status:</span>
                  </div>
                  {(['all', 'matched', 'underpayment', 'overpayment', 'unmatched', 'discrepancy'] as StatusFilter[]).map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatusFilter(s)}
                      className="px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                      style={{
                        backgroundColor: statusFilter === s ? 'rgba(212,160,23,0.15)' : 'var(--dark-elevated)',
                        color: statusFilter === s ? 'var(--accent)' : 'var(--dark-muted)',
                        border: `1px solid ${statusFilter === s ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                      }}
                    >
                      {s === 'all' ? 'All' : statusConfig[s]?.label ?? s}
                    </button>
                  ))}
                  <div className="flex items-center gap-1.5 ml-3 mr-2">
                    <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Channel:</span>
                  </div>
                  {(['All', 'Orange Money', 'MTN Money', 'Bank Transfer'] as ChannelFilter[]).map((ch) => (
                    <button
                      key={ch}
                      onClick={() => setChannelFilter(ch)}
                      className="px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                      style={{
                        backgroundColor: channelFilter === ch ? 'rgba(212,160,23,0.15)' : 'var(--dark-elevated)',
                        color: channelFilter === ch ? 'var(--accent)' : 'var(--dark-muted)',
                        border: `1px solid ${channelFilter === ch ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                      }}
                    >
                      {ch}
                    </button>
                  ))}
                </div>

                {/* Table */}
                <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid var(--dark-border)' }}>
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ backgroundColor: 'var(--dark-elevated)', borderBottom: '1px solid var(--dark-border)' }}>
                        {['Payment ID', 'Taxpayer', 'Bill Ref', 'Billed (USD)', 'Received (USD)', 'Variance', 'Channel', 'County', 'Date', 'Status'].map((h) => (
                          <th key={h} className="px-3 py-3 text-left font-semibold whitespace-nowrap" style={{ color: 'var(--dark-muted)' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((p) => {
                        const sc = statusConfig[p.status];
                        const variance = p.received - p.billed;
                        const varianceColor = variance === 0 ? '#34d399' : variance > 0 ? '#60a5fa' : '#f87171';
                        const channelColor = channelColors[p.channel] ?? 'var(--dark-muted)';
                        return (
                          <tr
                            key={p.id}
                            className="transition-colors"
                            style={{ borderBottom: '1px solid var(--dark-border)' }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.02)'; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                          >
                            <td className="px-3 py-3 font-mono-data font-semibold whitespace-nowrap" style={{ color: 'var(--accent)' }}>{p.id}</td>
                            <td className="px-3 py-3 font-medium whitespace-nowrap" style={{ color: 'var(--dark-text)' }}>{p.taxpayer}</td>
                            <td className="px-3 py-3 font-mono-data whitespace-nowrap" style={{ color: 'var(--dark-muted)' }}>{p.billRef}</td>
                            <td className="px-3 py-3 font-mono-data whitespace-nowrap" style={{ color: 'var(--dark-text)' }}>${p.billed.toLocaleString()}</td>
                            <td className="px-3 py-3 font-mono-data whitespace-nowrap" style={{ color: 'var(--dark-text)' }}>${p.received.toLocaleString()}</td>
                            <td className="px-3 py-3 font-mono-data font-semibold whitespace-nowrap" style={{ color: varianceColor }}>
                              {variance === 0 ? '—' : (variance > 0 ? '+' : '') + '$' + Math.abs(variance).toLocaleString()}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap">
                              <span className="font-semibold" style={{ color: channelColor }}>{p.channel}</span>
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--dark-muted)' }}>{p.county}</td>
                            <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--dark-muted)' }}>{p.date}</td>
                            <td className="px-3 py-3 whitespace-nowrap">
                              <span
                                className="flex items-center gap-1 px-2 py-0.5 rounded-full font-medium w-fit"
                                style={{ backgroundColor: sc.bg, color: sc.color }}
                              >
                                {sc.icon}
                                {sc.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Flag summary */}
                <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
                  {[
                    { status: 'underpayment', count: PAYMENT_RECORDS.filter((p) => p.status === 'underpayment').length, totalGap: PAYMENT_RECORDS.filter((p) => p.status === 'underpayment').reduce((a, p) => a + (p.billed - p.received), 0) },
                    { status: 'unmatched', count: PAYMENT_RECORDS.filter((p) => p.status === 'unmatched').length, totalGap: PAYMENT_RECORDS.filter((p) => p.status === 'unmatched').reduce((a, p) => a + p.billed, 0) },
                    { status: 'discrepancy', count: PAYMENT_RECORDS.filter((p) => p.status === 'discrepancy').length, totalGap: PAYMENT_RECORDS.filter((p) => p.status === 'discrepancy').reduce((a, p) => a + Math.abs(p.billed - p.received), 0) },
                  ].map((f) => {
                    const sc = statusConfig[f.status];
                    return (
                      <div
                        key={f.status}
                        className="rounded-xl p-4 flex items-center gap-3"
                        style={{ backgroundColor: sc.bg, border: `1px solid ${sc.color}30` }}
                      >
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${sc.color}20`, color: sc.color }}>
                          {sc.icon}
                        </div>
                        <div>
                          <p className="text-xs font-semibold" style={{ color: sc.color }}>{sc.label} ({f.count} records)</p>
                          <p className="text-sm font-bold font-mono-data mt-0.5" style={{ color: 'var(--dark-text)' }}>
                            ${f.totalGap.toLocaleString()} gap
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Collection Velocity Tab ── */}
            {activeTab === 'velocity' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>
                    Collection Velocity by Channel (USD thousands)
                  </p>
                  <div className="flex gap-1.5">
                    {(['daily', 'weekly'] as VelocityView[]).map((v) => (
                      <button
                        key={v}
                        onClick={() => setVelocityView(v)}
                        className="px-3 py-1.5 rounded-full text-xs font-medium transition-all capitalize"
                        style={{
                          backgroundColor: velocityView === v ? 'rgba(212,160,23,0.15)' : 'var(--dark-elevated)',
                          color: velocityView === v ? 'var(--accent)' : 'var(--dark-muted)',
                          border: `1px solid ${velocityView === v ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                        }}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={velocityData} barGap={4}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey={velocityKey} tick={{ fontSize: 11, fill: 'var(--dark-muted)' }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--dark-muted)' }} tickLine={false} axisLine={false} />
                      <Tooltip
                        contentStyle={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', borderRadius: '8px', fontSize: '12px' }}
                        formatter={(value: number) => [`$${value}K`, '']}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px', color: 'var(--dark-muted)' }} />
                      <Bar dataKey="orangeMoney" name="Orange Money" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="mtn" name="MTN Money" fill="#fbbf24" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="bank" name="Bank Transfer" fill="#60a5fa" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Weekly trend line */}
                <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
                  <p className="text-sm font-semibold mb-3" style={{ color: 'var(--dark-text)' }}>
                    Weekly Cumulative Trend
                  </p>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={WEEKLY_VELOCITY}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="week" tick={{ fontSize: 11, fill: 'var(--dark-muted)' }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--dark-muted)' }} tickLine={false} axisLine={false} />
                      <Tooltip
                        contentStyle={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', borderRadius: '8px', fontSize: '12px' }}
                        formatter={(value: number) => [`$${value}K`, '']}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px', color: 'var(--dark-muted)' }} />
                      <Line type="monotone" dataKey="orangeMoney" name="Orange Money" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="mtn" name="MTN Money" stroke="#fbbf24" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="bank" name="Bank Transfer" stroke="#60a5fa" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* ── County Breakdown Tab ── */}
            {activeTab === 'county' && (
              <div>
                <p className="text-sm font-semibold mb-4" style={{ color: 'var(--dark-text)' }}>
                  Payment Collection by County
                </p>
                <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={COUNTY_VELOCITY} layout="vertical" barGap={4}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--dark-muted)' }} tickLine={false} axisLine={false} />
                      <YAxis dataKey="county" type="category" tick={{ fontSize: 11, fill: 'var(--dark-muted)' }} tickLine={false} axisLine={false} width={110} />
                      <Tooltip
                        contentStyle={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', borderRadius: '8px', fontSize: '12px' }}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px', color: 'var(--dark-muted)' }} />
                      <Bar dataKey="matched" name="Matched" fill="#34d399" radius={[0, 3, 3, 0]} />
                      <Bar dataKey="flags" name="Flagged" fill="#fbbf24" radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid var(--dark-border)' }}>
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ backgroundColor: 'var(--dark-elevated)', borderBottom: '1px solid var(--dark-border)' }}>
                        {['County', 'Total Payments', 'Matched', 'Match Rate', 'Flagged', 'Flag Rate'].map((h) => (
                          <th key={h} className="px-4 py-3 text-left font-semibold" style={{ color: 'var(--dark-muted)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {COUNTY_VELOCITY.map((c) => {
                        const matchRate = ((c.matched / c.total) * 100).toFixed(1);
                        const flagRate = ((c.flags / c.total) * 100).toFixed(1);
                        const matchColor = parseFloat(matchRate) >= 90 ? '#34d399' : parseFloat(matchRate) >= 80 ? '#fbbf24' : '#f87171';
                        return (
                          <tr
                            key={c.county}
                            className="transition-colors"
                            style={{ borderBottom: '1px solid var(--dark-border)' }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.02)'; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                          >
                            <td className="px-4 py-3 font-medium flex items-center gap-2" style={{ color: 'var(--dark-text)' }}>
                              <MapPin size={12} style={{ color: 'var(--accent)' }} />
                              {c.county}
                            </td>
                            <td className="px-4 py-3 font-mono-data" style={{ color: 'var(--dark-text)' }}>{c.total.toLocaleString()}</td>
                            <td className="px-4 py-3 font-mono-data" style={{ color: '#34d399' }}>{c.matched.toLocaleString()}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--dark-border)', maxWidth: '80px' }}>
                                  <div className="h-full rounded-full" style={{ width: `${matchRate}%`, backgroundColor: matchColor }} />
                                </div>
                                <span className="font-mono-data font-semibold" style={{ color: matchColor }}>{matchRate}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 font-mono-data" style={{ color: '#fbbf24' }}>{c.flags}</td>
                            <td className="px-4 py-3 font-mono-data" style={{ color: '#fbbf24' }}>{flagRate}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
